import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { SubmitButton } from '../shared/SubmitButton';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import type { UntrackedAssetCatalogEntity } from '@peritasai/repository';
import type { InstrumentTaxonomyClassDto } from '@peritasai/types';
import {
  useCreateUntrackedAssetCatalog,
  useInstrumentClassImage,
  useInstrumentTaxonomyClasses,
  useUpdateTaxonomyLink,
  useUpdateUntrackedAssetCatalog,
} from '@peritasai/repository';
import { UntrackedAssetType } from '@peritasai/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';

interface UntrackedAssetCatalogDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedItem: UntrackedAssetCatalogEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  type: UntrackedAssetType;
  name: string;
  productNumber: string;
  manufacturer: string;
  description: string;
  cost: string;
  isActive: boolean;
  taxonomyClass: InstrumentTaxonomyClassDto | null;
}

interface FormErrors {
  type?: string;
  name?: string;
  productNumber?: string;
  manufacturer?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  type: UntrackedAssetType.SUPPLY,
  name: '',
  productNumber: '',
  manufacturer: '',
  description: '',
  cost: '',
  isActive: true,
  taxonomyClass: null,
};

function validate(
  form: FormState,
  mode: DrawerMode,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.untrackedAssetCatalogDrawer.nameLabel'),
    });
  }
  if (mode === DrawerMode.Create && !form.productNumber.trim()) {
    errors.productNumber = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.untrackedAssetCatalogDrawer.productNumberLabel'),
    });
  }
  if (mode === DrawerMode.Create && !form.manufacturer.trim()) {
    errors.manufacturer = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.untrackedAssetCatalogDrawer.manufacturerLabel'),
    });
  }
  return errors;
}

function TaxonomyClassOption({
  option,
  liProps,
}: {
  option: InstrumentTaxonomyClassDto;
  liProps: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
}) {
  const firstImage = option.images?.[0];
  const { data: imageSrc } = useInstrumentClassImage(firstImage?.id);

  return (
    <Box component="li" {...liProps} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 56, py: 1 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: '4px',
          overflow: 'hidden',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt={option.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>
      <Box>
        {option.category && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {option.category}
          </Typography>
        )}
        <Typography variant="body2">{option.name}</Typography>
      </Box>
    </Box>
  );
}

function TaxonomyInputAdornment({
  imageId,
  size = 24,
}: {
  imageId: number | undefined;
  size?: number;
}) {
  const { data: imageSrc } = useInstrumentClassImage(imageId ?? null);
  if (!imageSrc) return null;
  return (
    <Box
      component="img"
      src={imageSrc}
      sx={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: '2px',
        flexShrink: 0,
        ...(size === 24 && { mr: 0.5 }),
      }}
    />
  );
}

export const UntrackedAssetCatalogDrawer = ({
  open,
  mode,
  selectedItem,
  onClose,
  onSuccess,
}: UntrackedAssetCatalogDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateUntrackedAssetCatalog();
  const updateMutation = useUpdateUntrackedAssetCatalog();
  const taxonomyLinkMutation = useUpdateTaxonomyLink();
  const isPending =
    createMutation.isPending || updateMutation.isPending || taxonomyLinkMutation.isPending;

  const { data: taxonomyClasses = [], isLoading: taxonomyLoading } = useInstrumentTaxonomyClasses();

  const entityName = t('pages.entityManagement.untrackedAssetCatalogDrawer.entityName');
  const isInstrumentType = form.type === UntrackedAssetType.INSTRUMENT;

  // Look up the full taxonomy class (with images) from the loaded list.
  // The entity only stores instrumentTaxonomyClassId — images live on the taxonomy class itself.
  const fullTaxonomyClass =
    form.taxonomyClass != null
      ? (taxonomyClasses.find((c) => c.id === form.taxonomyClass?.id) ?? null)
      : null;

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedItem) {
      setForm({
        type: selectedItem.type,
        name: selectedItem.name,
        productNumber: selectedItem.productNumber ?? '',
        manufacturer: selectedItem.manufacturer ?? '',
        description: selectedItem.description ?? '',
        cost: selectedItem.cost != null ? String(selectedItem.cost) : '',
        isActive: selectedItem.isActive,
        taxonomyClass: selectedItem.taxonomyClass,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedItem]);

  const handleChange =
    (field: keyof Pick<FormState, 'name' | 'productNumber' | 'manufacturer' | 'description' | 'cost'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      if (touched) {
        setErrors(validate(next, mode, t));
      }
    };

  const handleTypeChange = (value: UntrackedAssetType) => {
    const next = {
      ...form,
      type: value,
      taxonomyClass: value !== UntrackedAssetType.INSTRUMENT ? null : form.taxonomyClass,
    };
    setForm(next);
    if (touched) {
      setErrors(validate(next, mode, t));
    }
  };

  const handleTaxonomyChange = (_: React.SyntheticEvent, value: InstrumentTaxonomyClassDto | null) => {
    setForm((prev) => ({ ...prev, taxonomyClass: value }));
  };

  const handleIsActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, mode, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const costValue =
      form.cost.trim().length > 0 ? parseFloat(form.cost.trim()) : null;

    try {
      if (mode === DrawerMode.Create) {
        const result = await createMutation.mutateAsync({
          type: form.type,
          name: form.name.trim(),
          productNumber: form.productNumber.trim(),
          manufacturer: form.manufacturer.trim(),
          ...(form.description.trim().length > 0 && { description: form.description.trim() }),
          ...(costValue != null && !Number.isNaN(costValue) && { cost: costValue }),
          isActive: form.isActive,
          instrumentTaxonomyClassId: isInstrumentType
            ? (form.taxonomyClass?.id ?? null)
            : undefined,
        });

        setForm(emptyForm);

        if (result.taxonomyError) {
          setErrors({
            apiError: `${t('pages.entityManagement.drawer.createSuccessTaxonomyError')}: ${result.taxonomyError}`,
          });
        } else {
          setTouched(false);
        }

        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedItem) {
        await updateMutation.mutateAsync({
          id: selectedItem.id,
          dto: {
            type: form.type,
            name: form.name.trim(),
            ...(form.description.trim().length > 0
              ? { description: form.description.trim() }
              : { description: null }),
            cost: costValue != null && !Number.isNaN(costValue) ? costValue : null,
            isActive: form.isActive,
          },
        });

        const taxonomyChanged =
          (form.taxonomyClass?.id ?? null) !== (selectedItem.instrumentTaxonomyClassId ?? null);
        if ((isInstrumentType || selectedItem.isInstrument) && taxonomyChanged) {
          try {
            await taxonomyLinkMutation.mutateAsync({
              catalogItemId: selectedItem.id,
              dto: { instrumentTaxonomyClassId: form.taxonomyClass?.id ?? null },
            });
          } catch (taxonomyErr) {
            const message =
              taxonomyErr instanceof Error
                ? taxonomyErr.message
                : t('pages.entityManagement.drawer.errorUnexpected');
            setErrors((prev) => ({ ...prev, apiError: message }));
            return;
          }
        }

        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        {/* Row 1: Name + Manufacturer */}
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.untrackedAssetCatalogDrawer.nameLabel')} *`}
            fullWidth
            size="small"
            value={form.name}
            onChange={handleChange('name')}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.untrackedAssetCatalogDrawer.manufacturerLabel')} *`}
            fullWidth
            size="small"
            value={form.manufacturer}
            onChange={handleChange('manufacturer')}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.manufacturer}
            helperText={touched ? errors.manufacturer : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        {/* Row 2: Product Number + Type */}
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.untrackedAssetCatalogDrawer.productNumberLabel')} *`}
            fullWidth
            size="small"
            value={form.productNumber}
            onChange={handleChange('productNumber')}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.productNumber}
            helperText={touched ? errors.productNumber : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <FormControl fullWidth size="small" disabled={mode === DrawerMode.Edit}>
            <InputLabel id="uac-type-label">
              {t('pages.entityManagement.untrackedAssetCatalogDrawer.typeLabel')} *
            </InputLabel>
            <Select
              labelId="uac-type-label"
              label={`${t('pages.entityManagement.untrackedAssetCatalogDrawer.typeLabel')} *`}
              value={form.type}
              onChange={(e) => handleTypeChange(e.target.value as UntrackedAssetType)}
            >
              {Object.values(UntrackedAssetType).map((typeValue) => (
                <MenuItem key={typeValue} value={typeValue}>
                  {t(`pages.entityManagement.untrackedAssetCatalog.type.${typeValue}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Row 3: Description + Cost */}
        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.untrackedAssetCatalogDrawer.descriptionLabel')}
            fullWidth
            size="small"
            value={form.description}
            onChange={handleChange('description')}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.untrackedAssetCatalogDrawer.costLabel')}
            fullWidth
            size="small"
            type="number"
            value={form.cost}
            onChange={handleChange('cost')}
            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
          />
        </Grid>

        {/* Row 4: Active toggle */}
        <Grid size={12}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {t('pages.entityManagement.untrackedAssetCatalogDrawer.isActiveLabel')}
          </Typography>
          <Switch checked={form.isActive} onChange={handleIsActiveChange} sx={{ ml: '-11px' }} />
        </Grid>

        {/* Row 5: Taxonomy class (INSTRUMENT type only) */}
        {isInstrumentType && mode === DrawerMode.Create && (
          <Grid size={12}>
            <Autocomplete
              options={taxonomyClasses}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={form.taxonomyClass}
              onChange={handleTaxonomyChange}
              loading={taxonomyLoading}
              renderOption={(props, option) => {
                const { key, ...liProps } = props;
                return <TaxonomyClassOption key={key} option={option} liProps={liProps} />;
              }}
              renderInput={(params) => {
                const firstImage = form.taxonomyClass?.images?.[0];
                return (
                  <TextField
                    {...params}
                    label={t('pages.entityManagement.untrackedAssetCatalogDrawer.taxonomySearchLabel')}
                    size="small"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <TaxonomyInputAdornment imageId={firstImage?.id} />
                            {params.InputProps?.startAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                );
              }}
            />
          </Grid>
        )}

        {isInstrumentType && mode === DrawerMode.Edit && (
          <Grid size={12}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {t('pages.entityManagement.untrackedAssetCatalogDrawer.taxonomyClassLabel')} *
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  flexShrink: 0,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TaxonomyInputAdornment imageId={fullTaxonomyClass?.images?.[0]?.id} size={52} />
              </Box>
              <Box>
                {(fullTaxonomyClass?.category ?? selectedItem?.instrumentTaxonomyCategoryName) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {fullTaxonomyClass?.category ?? selectedItem?.instrumentTaxonomyCategoryName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {form.taxonomyClass?.name ?? '—'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
